import {DailyPeriodSummary, HourlyWeatherForecastItem} from '../../api/types';
import * as Dates from '../Dates';

/**
 * Time-of-day periods, mirroring `apps/core/utils/weather/periods.py` on the
 * backend. These are the same boundaries the API already uses to build the
 * daily DESCRIPTION sentence ("Cloudy, rain in the evening"), so a period tile
 * and the sentence printed above it always describe the same hours.
 */
export type DayPeriod = 'night' | 'morning' | 'afternoon' | 'evening';

/** Clock order — a civil day starts inside `night`. */
export const PERIOD_ORDER: readonly DayPeriod[] = [
  'night',
  'morning',
  'afternoon',
  'evening',
];

/**
 * Four equal six-hour quarters of one civil day.
 *
 * `evening` deliberately reaches midnight rather than stopping at 22:00. With a
 * four-hour evening, `night` covered 00:00-05:59 *and* 22:00-23:59, so one
 * period mapped to two disjoint blocks: the "Nuit" tile aggregated both ends of
 * the day and printed the late evening's rain — and its rain icon — over six dry
 * small hours.
 *
 * These are the quarters Environnement Canada and Meteo-France publish (matin
 * 6h-12h, apres-midi 12h-18h, soir 18h-minuit, nuit minuit-6h). Mirrors
 * `get_period` in `apps/core/utils/weather/periods.py`; the two must agree, or
 * the tile and the sentence above it describe different hours.
 */
export const getPeriod = (hour: number): DayPeriod => {
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 24) return 'evening';
  return 'night';
};

/**
 * The contiguous clock blocks a 00:00 → 23:00 list is cut into.
 *
 * One block per period, in clock order — see `getPeriod` for why the evening
 * reaches midnight. Each period therefore has a single icon describing a single
 * run of rows, which is what lets a section header and its hours agree.
 */
export type PeriodBlock = {
  period: DayPeriod;
  startHour: number;
  endHour: number;
};

export const PERIOD_BLOCKS: readonly PeriodBlock[] = [
  {period: 'night', startHour: 0, endHour: 5},
  {period: 'morning', startHour: 6, endHour: 11},
  {period: 'afternoon', startHour: 12, endHour: 17},
  {period: 'evening', startHour: 18, endHour: 23},
];

/** Hour of day at the forecast location, not on the device. */
export const hourOf = (timestamp: number, timezone: string): number =>
  Dates.timestampToMoment(timestamp, timezone).hour();

export type PeriodAggregate = {
  period: DayPeriod;
  hours: HourlyWeatherForecastItem[];
  tMin: number | null;
  tMax: number | null;
  rain: number;
  gust: number | null;
};

/** A `PeriodAggregate` that knows which contiguous clock block it covers. */
export type PeriodBlockAggregate = PeriodAggregate & PeriodBlock;

const aggregate = (
  period: DayPeriod,
  hours: HourlyWeatherForecastItem[],
): PeriodAggregate => {
  const temps = hours.map((h) => h.T).filter((t): t is number => t != null);
  const gusts = hours.map((h) => h.FXY).filter((g): g is number => g != null);
  return {
    period,
    hours,
    tMin: temps.length ? Math.min(...temps) : null,
    tMax: temps.length ? Math.max(...temps) : null,
    rain: hours.reduce((sum, h) => sum + (h.RR ?? 0), 0),
    gust: gusts.length ? Math.max(...gusts) : null,
  };
};

/**
 * Aggregate one day's hourly rows into the four periods, in clock order.
 *
 * Periods with no hours are dropped — a day at the end of the forecast horizon
 * can stop mid-afternoon. Used for the header numbers, and as the fallback for
 * everything except the icon when the payload predates `PERIODS`.
 */
export const aggregatePeriods = (
  hourly: HourlyWeatherForecastItem[],
  timezone: string,
): PeriodAggregate[] => {
  const buckets = new Map<DayPeriod, HourlyWeatherForecastItem[]>();
  hourly.forEach((item) => {
    const period = getPeriod(hourOf(item.timestamp, timezone));
    const bucket = buckets.get(period);
    if (bucket) bucket.push(item);
    else buckets.set(period, [item]);
  });
  // PERIOD_ORDER *is* clock order now that each period is one contiguous block,
  // so it needs no re-sorting. This used to sort by `hours[0].timestamp` to keep
  // the 22-23 night from being drawn as the first tile; on unordered input that
  // would order by first-encountered row rather than by earliest hour, which is
  // strictly worse than the fixed order it was correcting.
  return PERIOD_ORDER.filter((period) => buckets.get(period)?.length).map(
    (period) =>
      aggregate(period, buckets.get(period) as HourlyWeatherForecastItem[]),
  );
};

/** Aggregate a single contiguous block (a section of the list). */
export const aggregateBlock = (
  hourly: HourlyWeatherForecastItem[],
  block: PeriodBlock,
  timezone: string,
): PeriodBlockAggregate => ({
  ...aggregate(
    block.period,
    hourly.filter((item) => {
      const hour = hourOf(item.timestamp, timezone);
      return hour >= block.startHour && hour <= block.endHour;
    }),
  ),
  ...block,
});

/**
 * The blocks that actually have hours, in clock order.
 *
 * One pass: `aggregateBlock` filters the whole day and reads the clock per item,
 * so calling it once per block cost a full pass and 24 timezone conversions each
 * — paid again on every mode toggle, for every day the user has swiped through.
 */
export const buildPeriodBlocks = (
  hourly: HourlyWeatherForecastItem[],
  timezone: string,
): PeriodBlockAggregate[] => {
  const buckets: HourlyWeatherForecastItem[][] = PERIOD_BLOCKS.map(() => []);
  hourly.forEach((item) => {
    const hour = hourOf(item.timestamp, timezone);
    const index = PERIOD_BLOCKS.findIndex(
      (block) => hour >= block.startHour && hour <= block.endHour,
    );
    if (index >= 0) buckets[index].push(item);
  });
  return PERIOD_BLOCKS.map((block, index) => ({
    ...aggregate(block.period, buckets[index]),
    ...block,
  })).filter((entry) => entry.hours.length > 0);
};

/**
 * Icon for a period, from the backend when it is there.
 *
 * Deliberately no client-side fallback: the icon comes out of a severity
 * decision tree that lives in `compute_daily_weather_icon`, and a second
 * implementation here would be a second thing to keep in agreement. A payload
 * without `PERIODS` renders the tiles without icons instead of with wrong ones.
 */
export const periodIcon = (
  summaries: DailyPeriodSummary[] | undefined,
  period: DayPeriod,
): number | null =>
  summaries?.find((entry) => entry.period === period)?.ICON ?? null;
